import axios from 'axios';
import { loginPathFor } from '../utils/loginPath';

const API_URL = process.env.REACT_APP_API_URL || '/api/v1';

const api = axios.create({
  baseURL: API_URL,
  timeout: 15000,
  headers: { 'Content-Type': 'application/json' },
});

// Unauthenticated receipt link — safe to paste into a WhatsApp/SMS message sent to a
// customer, unlike paymentAPI/agentAppAPI's getReceiptUrl which requires a staff/agent login
// and gives the customer "Failed to load PDF document" when they open it.
// Short /r/<id> form (not /api/v1/public/<id>/receipt) so what the customer actually sees
// is a plain link, not a raw backend API path — see backend server.js's `/r` mount and
// utils/messageTemplate.js's buildReceiptLink, which builds the same shape server-side.
// Takes the payment (uses its random publicToken when it has one) or a plain id for old rows.
export const getPublicReceiptUrl = (payment) => `/r/${payment?.publicToken || payment?._id || payment}`;

// Request interceptor - attach token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor - handle 401
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response?.status === 401 && error.response?.data?.code === 'TOKEN_EXPIRED') {
      try {
        const refreshToken = localStorage.getItem('refreshToken');
        const { data } = await axios.post(`${API_URL}/auth/refresh-token`, { refreshToken });
        localStorage.setItem('token', data.data.token);
        localStorage.setItem('refreshToken', data.data.refreshToken);
        error.config.headers.Authorization = `Bearer ${data.data.token}`;
        return api(error.config);
      } catch {
        localStorage.clear();
        // /login is the staff/CRM login page — a customer-portal session (/portal) or an
        // agent session (/agent) must bounce back to its own login instead, or
        // app/agent.laxmicablevision.in end up showing crm.laxmicablevision.in's login screen.
        window.location.href = loginPathFor(window.location.pathname);
      }
    }
    return Promise.reject(error);
  }
);

// ─── Auth ──────────────────────────────────────────────────────────────────────
export const authAPI = {
  login: (data) => api.post('/auth/login', data),
  customerLogin: (data) => api.post('/auth/customer-login', data),
  logout: () => api.post('/auth/logout'),
  me: () => api.get('/auth/me'),
  changePassword: (data) => api.put('/auth/change-password', data),
  setAppearance: (appearance) => api.put('/auth/me/appearance', { appearance }),
};

// ─── Insights (admin) ──────────────────────────────────────────────────────────
export const insightsAPI = {
  overview: (service = 'all') => api.get('/insights/overview', { params: { service } }),
  agents: (month) => api.get('/insights/agents', { params: month ? { month } : {} }),
  network: (fresh) => api.get('/insights/network', { params: fresh ? { fresh: 1 } : {} }),
  recordDeposit: (data) => api.post('/insights/deposits', data),
  deposits: (agent) => api.get('/insights/deposits', { params: { agent } }),
  deleteDeposit: (id) => api.delete(`/insights/deposits/${id}`),
};

// ─── Customers ─────────────────────────────────────────────────────────────────
export const customerAPI = {
  getAll: (params) => api.get('/customers', { params }),
  // Cross-type search (top nav, floating pay button, etc). A plain `search` call sorts by
  // billingNumber and applies `limit` across ALL types together — since broadband billing
  // numbers run much lower than CATV's, any common term fills the whole page with broadband
  // matches and CATV customers never surface. Query each type separately and interleave so
  // both are represented within `limit`.
  search: async (q, limit = 8) => {
    const half = Math.ceil(limit / 2);
    const [catv, broadband] = await Promise.all([
      api.get('/customers', { params: { search: q, customerType: 'catv,both', limit: half } }),
      api.get('/customers', { params: { search: q, customerType: 'broadband,both', limit: half } }),
    ]);
    const a = catv.data.data || [];
    const b = broadband.data.data || [];
    const seen = new Set();
    const merged = [];
    for (let i = 0; i < Math.max(a.length, b.length) && merged.length < limit; i++) {
      for (const c of [a[i], b[i]]) {
        if (c && !seen.has(c._id) && merged.length < limit) { seen.add(c._id); merged.push(c); }
      }
    }
    return merged;
  },
  getById: (id) => api.get(`/customers/${id}`),
  getByStb: (serial) => api.get(`/customers/by-stb/${encodeURIComponent(serial)}`),
  create: (data) => api.post('/customers', data),
  update: (id, data) => api.put(`/customers/${id}`, data),
  updateStatus: (id, data) => api.put(`/customers/${id}/status`, data),
  updateBroadbandStatus: (id, data) => api.put(`/customers/${id}/broadband-status`, data),
  updateCatvStatus: (id, data) => api.put(`/customers/${id}/catv-status`, data),
  adjustBalance: (id, data) => api.post(`/customers/${id}/adjust-balance`, data),
  // Admin-only manual expiry override — see routes/customers.js edit-validity route comment.
  editValidity: (id, data) => api.post(`/customers/${id}/edit-validity`, data),
  sendReminder: (id, templateIndex) => api.post(`/customers/${id}/send-reminder`, templateIndex != null ? { templateIndex } : {}),
  // Renewal Dashboard's Call Couldn't Connect / Send Plans / Send QR buttons — category is
  // 'call_no_answer' | 'plans' | 'payment_qr' (see MessageTemplate model + Message Center).
  sendPortalLogin: (id) => api.post(`/customers/${id}/send-portal-login`),
  sendTemplate: (id, category) => api.post(`/customers/${id}/send-template`, { category }),
  sendPaymentQr: (id, data) => api.post(`/customers/${id}/send-payment-qr`, data),
  delete: (id) => api.delete(`/customers/${id}`),
  deleteMany: (ids) => api.delete('/customers/bulk', { data: { ids } }),
  getPayments: (id) => api.get(`/customers/${id}/payments`),
  getComplaints: (id) => api.get(`/customers/${id}/complaints`),
  getAreas: (params) => api.get('/customers/areas', { params }),
  getNodes: () => api.get('/customers/nodes'),
  getStbModels: () => api.get('/customers/stb-models'),
  getEntityCodes: () => api.get('/customers/entity-codes'),
  getIspNames: () => api.get('/customers/isp-names'),
  getStats: () => api.get('/customers/stats'),
  checkAccountNumber: (accountNumber, excludeId) => api.get('/customers/check-account', { params: { accountNumber, excludeId } }),
  checkPPPoE: (username, excludeId) => api.get('/customers/check-pppoe', { params: { username, excludeId } }),
  checkBillingNumber: (billingNumber, customerType, excludeId) => api.get('/customers/check-billing', { params: { billingNumber, customerType, excludeId } }),
  nextBillingNumber: (customerType) => api.get('/customers/next-billing', { params: { customerType } }),
  getMe: (accountId) => api.get('/customers/me', { params: accountId ? { accountId } : {} }),
  getMyAccounts: () => api.get('/customers/me/accounts'),
  updateMe: (data) => api.put('/customers/me', data),
  submitServiceRequest: (data) => api.post('/customers/me/service-request', data),
  requestServiceInterest: (serviceType) => api.post('/customers/me/service-interest', { serviceType }),
  submitReferral: (data) => api.post('/customers/me/refer', data),
  getMyBandwidth: () => api.get('/customers/me/bandwidth'),
  getMyUsage: () => api.get('/customers/me/usage'),
  getMyUsageSessions: () => api.get('/customers/me/usage-sessions'),
  getMyDeviceStatus: () => api.get('/customers/me/device-status'),
  syncMyDeviceStatus: () => api.post('/customers/me/device-status/sync', {}, { timeout: 45000 }),
  getMyWifi: () => api.get('/customers/me/wifi'),
  updateMyWifi: (data) => api.post('/customers/me/wifi', data, { timeout: 45000 }), // ACS waits up to 30s for the router
  retrackStb: () => api.post('/customers/me/retrack-stb', {}, { timeout: 30000 }),
  createRenewOrder: (data) => api.post('/customers/me/renew/create-order', data),
  verifyRenewPayment: (data) => api.post('/customers/me/renew/verify', data),
  createBalanceOrder: (data) => api.post('/customers/me/balance/create-order', data),
  createCatvDtdOrder: (data) => api.post('/customers/me/catv-dtd/create-order', data),
  getPushPublicKey: () => api.get('/customers/me/push/public-key'),
  getPushStatus: (endpoint) => api.get('/customers/me/push/status', { params: { endpoint } }),
  subscribePush: (subscription) => api.post('/customers/me/push/subscribe', { subscription }),
  unsubscribePush: (endpoint) => api.post('/customers/me/push/unsubscribe', { endpoint }),
  listNotifications: (params) => api.get('/notifications', { params }),
  getUnreadNotificationCount: () => api.get('/notifications/unread-count'),
  markNotificationRead: (id) => api.put(`/notifications/${id}/read`),
  markAllNotificationsRead: () => api.put('/notifications/read-all'),
  // Content-Type must be explicitly unset (not omitted) — see adsAPI.upload's comment.
  uploadPaymentScreenshot: (formData) => api.post('/customers/me/payment-screenshot', formData, { headers: { 'Content-Type': undefined }, timeout: 60000 }),
  linkAccount: (id, linkedAccountId) => api.put(`/customers/${id}/link-account`, { linkedAccountId }),
  getLinkSuggestions: () => api.get('/customers/link-suggestions', { timeout: 30000 }),
  autoLinkSuggestions: () => api.post('/customers/link-suggestions/auto-link', {}, { timeout: 30000 }),
  // See adsAPI.upload's comment — Content-Type must be explicitly unset, not omitted.
  uploadDocuments: (id, formData) => api.post(`/customers/${id}/documents`, formData, { headers: { 'Content-Type': undefined }, timeout: 120000 }),
  deleteDocument: (id, docId) => api.delete(`/customers/${id}/documents/${docId}`),
  getDocumentBlob: (id, docId) => api.get(`/customers/${id}/documents/${docId}/file`, { responseType: 'blob' }),
  // A selected-rows export (`ids`) can run into the hundreds — as a GET query string that
  // blows nginx's default 8k header buffer (a bare 414, before the request even reaches the
  // backend) well before it'd hit any server-side limit. POST's body has no such cap, so any
  // `ids` goes there instead; the plain "new since last export" case (no ids) stays a GET.
  exportContacts: ({ ids, ...params } = {}) => ids
    ? api.post('/customers/export-contacts', { ...params, ids: Array.isArray(ids) ? ids : String(ids).split(',') }, { responseType: 'blob' })
    : api.get('/customers/export-contacts', { params, responseType: 'blob' }),
  unlinkAccount: (id) => api.delete(`/customers/${id}/link-account`),
  setLinkedCatvAccounts: (id, catvCustomerIds) => api.put(`/customers/${id}/linked-catv-accounts`, { catvCustomerIds }),
  getPortalLogin: (id) => api.get(`/customers/${id}/portal-login`),
  setPortalLoginMobile: (id, mobile) => api.put(`/customers/${id}/portal-login`, { mobile }),
  resetPortalPassword: (id, password) => api.post(`/customers/${id}/portal-login/reset-password`, password ? { password } : {}),
  getNeedsPlan: (scope) => api.get('/customers/needs-plan', { params: { scope } }),
  assignPlan: (id, planId) => api.put(`/customers/${id}/assign-plan`, { planId }),
  autoAssignPlans: (scope) => api.post('/customers/needs-plan/auto-assign', {}, { params: { scope }, timeout: 30000 }),
};

// ─── Config ────────────────────────────────────────────────────────────────────
export const configAPI = {
  get: () => axios.get('/api/v1/config'),
};

// ─── Public (unauthenticated marketing site) ────────────────────────────────────
export const publicAPI = {
  getPlans: () => axios.get('/api/v1/public/plans'),
  submitConnectionRequest: (data) => axios.post('/api/v1/public/connection-requests', data),
};

// ─── Leads (public "Get a new connection" form + field agents canvassing door-to-door) ──
export const connectionRequestAPI = {
  getAll: (params) => api.get('/connection-requests', { params }),
  getStats: () => api.get('/connection-requests/stats'),
  create: (data) => api.post('/connection-requests', data),
  updateStatus: (id, data) => api.put(`/connection-requests/${id}/status`, data),
  deleteRequest: (id) => api.put(`/connection-requests/${id}/delete`),
  restoreRequest: (id) => api.put(`/connection-requests/${id}/restore`),
  sendPlans: (id) => api.put(`/connection-requests/${id}/send-plans`),
  sendQr: (id) => api.put(`/connection-requests/${id}/send-qr`),
  sendDocs: (id) => api.put(`/connection-requests/${id}/send-docs`),
  setFollowUpDaily: (id, enabled) => api.put(`/connection-requests/${id}/follow-up-daily`, { enabled }),
};

// ─── Reconnection Leads (inactive CableTV customers the office calls back) ─────
export const reconnectionLeadAPI = {
  getAll: () => api.get('/reconnection-leads'),
  getDiscontinued: () => api.get('/reconnection-leads/discontinued'),
  syncDiscontinued: () => api.post('/reconnection-leads/discontinued/sync'),
  setDiscontinuedHidden: (leadKey, hidden) => api.put('/reconnection-leads/discontinued/hidden', { leadKey, hidden }),
  setBoxStatus: (accountNumber, boxStatus) => api.put(`/reconnection-leads/${encodeURIComponent(accountNumber)}/box-status`, { boxStatus }),
  // data: { leadKey, text, remindAt?, label? }
  addNote: (data) => api.post('/reconnection-leads/notes', data),
  setStatus: (accountNumber, status) => api.put(`/reconnection-leads/${encodeURIComponent(accountNumber)}/status`, { status }),
};

// ─── Broadband Plans ───────────────────────────────────────────────────────────
export const broadbandAPI = {
  getPlans: (params) => api.get('/broadband', { params }),
  createPlan: (data) => api.post('/broadband', data),
  updatePlan: (id, data) => api.put(`/broadband/${id}`, data),
  deletePlan: (id) => api.delete(`/broadband/${id}`),
  assignPlan: (data) => api.post('/broadband/assign', data),
  addAddon: (customerId, planId) => api.post(`/broadband/customers/${customerId}/addons`, { planId }),
  removeAddon: (customerId, entryId) => api.delete(`/broadband/customers/${customerId}/addons/${entryId}`),
  // Content-Type must be explicitly unset (not a fixed 'multipart/form-data' string,
  // which has no boundary) — see adsAPI.upload's comment for why.
  uploadPdf: (id, file) => {
    const fd = new FormData();
    fd.append('pdf', file);
    return api.post(`/broadband/${id}/pdf`, fd, { headers: { 'Content-Type': undefined } });
  },
};

// ─── CATV ──────────────────────────────────────────────────────────────────────
export const catvAPI = {
  getPackages: (params) => api.get('/catv', { params }),
  createPackage: (data) => api.post('/catv', data),
  updatePackage: (id, data) => api.put(`/catv/${id}`, data),
  deletePackage: (id) => api.delete(`/catv/${id}`),
  assignPackage: (data) => api.post('/catv/assign', data),
  // Content-Type must be explicitly unset (not a fixed 'multipart/form-data' string,
  // which has no boundary) — see adsAPI.upload's comment for why.
  uploadPdf: (id, file) => {
    const fd = new FormData();
    fd.append('pdf', file);
    return api.post(`/catv/${id}/pdf`, fd, { headers: { 'Content-Type': undefined } });
  },
};

// ─── Payments ──────────────────────────────────────────────────────────────────
// Advance receipts — payment collected before the connection is done (see backend advanceReceiptService).
export const advanceReceiptAPI = {
  getAll: (params) => api.get('/advance-receipts', { params }),
  create: (data) => api.post('/advance-receipts', data),
  getPdf: (id) => api.get(`/advance-receipts/${id}/pdf`, { responseType: 'blob' }),
  resendWhatsapp: (id) => api.post(`/advance-receipts/${id}/whatsapp`),
  apply: (id, customerId) => api.post(`/advance-receipts/${id}/apply`, { customerId }),
  cancel: (id, reason) => api.post(`/advance-receipts/${id}/cancel`, { reason }),
};

export const paymentAPI = {
  getAll: (params) => api.get('/payments', { params }),
  create: (data) => api.post('/payments', data),
  getById: (id) => api.get(`/payments/${id}`),
  getSummary: () => api.get('/payments/summary'),
  exportCollections: (params) => api.get('/payments/export', { params, responseType: 'blob' }),
  getAgentSummary: (period) => api.get('/payments/agent-summary', { params: { period } }),
  getCollectionPending: (params) => api.get('/payments/collection-pending', { params }),
  getCollectionPendingLatest: (customerIds) => api.get('/payments/collection-pending/latest', { params: { customerIds: customerIds.join(',') } }),
  getMyToday: () => api.get('/payments/my-today'),
  getReceiptUrl: (id) => `${API_URL}/payments/${id}/receipt`,
  getReceiptBlob: (id) => api.get(`/payments/${id}/receipt`, { responseType: 'blob' }),
  shareWhatsApp: (id) => api.post(`/payments/${id}/share-whatsapp`),
  // Admin-only fix of a renewal entry's From/To — see routes/payments.js PATCH :id/validity.
  editValidity: (id, data) => api.patch(`/payments/${id}/validity`, data),
  update: (id, data) => api.put(`/payments/${id}`, data),
  transferService: (id, to) => api.patch(`/payments/${id}/transfer-service`, { to }),
  reassignCollector: (data) => api.patch('/payments/reassign-collector', data),
  delete: (id) => api.delete(`/payments/${id}`),
  deleteMany: (ids) => api.delete('/payments/bulk', { data: { ids } }),
  getLedger: (customerId) => api.get(`/payments/customer/${customerId}/ledger`),
  deleteLedgerBill: (customerId, billId) => api.delete(`/payments/customer/${customerId}/ledger/bills/${billId}`),
  deleteLedgerPayment: (customerId, paymentId) => api.delete(`/payments/customer/${customerId}/ledger/payments/${paymentId}`),
  deleteLedgerAttempt: (customerId, attemptId) => api.delete(`/payments/customer/${customerId}/ledger/attempts/${attemptId}`),
  deleteLedgerAdjustment: (customerId, changeLogId) => api.delete(`/payments/customer/${customerId}/ledger/adjustments/${changeLogId}`),
  getScreenshots: (params) => api.get('/payments/screenshots', { params }),
  getScreenshotBlob: (id) => api.get(`/payments/screenshots/${id}/file`, { responseType: 'blob' }),
  acceptScreenshot: (id, data) => api.post(`/payments/screenshots/${id}/accept`, data),
  rejectScreenshot: (id, data) => api.post(`/payments/screenshots/${id}/reject`, data),
  deleteScreenshot: (id) => api.delete(`/payments/screenshots/${id}`),
  getOnline: (params) => api.get('/payments/online', { params }),
  checkSettlement: (orderIds) => api.post('/payments/online/check-settlement', orderIds ? { orderIds } : {}),
};

// ─── Renewals ──────────────────────────────────────────────────────────────────
export const renewalAPI = {
  getUpcoming: (params) => api.get('/renewals/upcoming', { params }),
  getExpired: (params) => api.get('/renewals/expired', { params }),
  getRange: (params) => api.get('/renewals/range', { params }),
  getHistory: (params) => api.get('/renewals/history', { params }),
  getDashboard: () => api.get('/renewals/dashboard'),
  getNotes: (customerId, serviceType) => api.get(`/renewals/notes/${customerId}`, { params: { serviceType } }),
  addNote: (data) => api.post('/renewals/notes', data),
};

// ─── Complaints ────────────────────────────────────────────────────────────────
export const complaintAPI = {
  getAll: (params) => api.get('/complaints', { params }),
  getById: (id) => api.get(`/complaints/${id}`),
  create: (data) => api.post('/complaints', data, data instanceof FormData ? { headers: { 'Content-Type': undefined } } : undefined),
  assign: (id, data) => api.put(`/complaints/${id}/assign`, data),
  updateStatus: (id, data) => api.put(`/complaints/${id}/update-status`, data, data instanceof FormData ? { headers: { 'Content-Type': undefined } } : undefined),
  addComment: (id, data) => api.post(`/complaints/${id}/comment`, data, data instanceof FormData ? { headers: { 'Content-Type': undefined } } : undefined),
  addAttachment: (id, formData) => api.post(`/complaints/${id}/attachments`, formData, { headers: { 'Content-Type': undefined } }),
  getStats: () => api.get('/complaints/stats'),
  getAssignableStaff: () => api.get('/complaints/assignable-staff'),
};

// ─── Service Requests (disconnection / reconnection / package change / relocation) ──
export const serviceRequestAPI = {
  getAll: (params) => api.get('/service-requests', { params }),
  getById: (id) => api.get(`/service-requests/${id}`),
  create: (data) => api.post('/service-requests', data),
  update: (id, data) => api.put(`/service-requests/${id}`, data),
  approve: (id, data) => api.put(`/service-requests/${id}/approve`, data),
  reject: (id, data) => api.put(`/service-requests/${id}/reject`, data),
  setGtplDone: (id, done = true) => api.put(`/service-requests/${id}/gtpl-done`, { done }),
  getStats: () => api.get('/service-requests/stats'),
  getMonthlySummary: (params) => api.get('/service-requests/monthly-summary', { params }),
};

// ─── Notes (personal WhatsApp-reminder notepad) ─────────────────────────────────
// create/update take a FormData when an image is attached — Content-Type: undefined lets
// axios/the browser set the multipart boundary itself instead of the shared instance's
// default application/json (same pattern as adsAPI.upload).
export const noteAPI = {
  getAll: () => api.get('/notes'),
  create: (data) => api.post('/notes', data, data instanceof FormData ? { headers: { 'Content-Type': undefined } } : undefined),
  update: (id, data) => api.put(`/notes/${id}`, data, data instanceof FormData ? { headers: { 'Content-Type': undefined } } : undefined),
  remove: (id) => api.delete(`/notes/${id}`),
  getSettings: () => api.get('/notes/settings'),
  updateSettings: (data) => api.put('/notes/settings', data),
};

// ─── Devices ───────────────────────────────────────────────────────────────────
export const deviceAPI = {
  getAll: (params) => api.get('/devices', { params }),
  getById: (id) => api.get(`/devices/${id}`),
  create: (data) => api.post('/devices', data),
  update: (id, data) => api.put(`/devices/${id}`, data),
  install: (id, data) => api.post(`/devices/${id}/install`, data),
  getStats: () => api.get('/devices/stats'),
  bulkSync: (devices) => api.post('/devices/bulk-sync', { devices }),
  bulkDelete: (ids) => api.post('/devices/bulk-delete', { ids }),
  syncStatus: () => api.post('/devices/sync-status'),
};

// ─── Dashboard ─────────────────────────────────────────────────────────────────
export const dashboardAPI = {
  getOverview: () => api.get('/dashboard/overview'),
  getMonthlyGrowth: () => api.get('/dashboard/monthly-growth'),
  getAreaStats: () => api.get('/dashboard/area-stats'),
  getRecentActivity: () => api.get('/dashboard/recent-activity'),
  getBandwidth: () => api.get('/nas/bandwidth'),
};

// ─── Reports ───────────────────────────────────────────────────────────────────
export const reportAPI = {
  dailyCollection: (params) => api.get('/reports/daily-collection', { params }),
  monthlyCollection: (params) => api.get('/reports/monthly-collection', { params }),
  dueCustomers: () => api.get('/reports/due-customers'),
  areaWise: () => api.get('/reports/area-wise'),
  complaints: (params) => api.get('/reports/complaints', { params }),
  renewal: (params) => api.get('/reports/renewal', { params }),
  collectionSheet: (params) => api.get('/reports/collection-sheet', { params }),
  collectionSheetExport: (params) => api.get('/reports/collection-sheet/export', { params, responseType: 'blob' }),
  collectionSheetSocieties: () => api.get('/reports/collection-sheet/societies'),
  collectionSheetAgents: () => api.get('/reports/collection-sheet/agents'),
  collectionSheetAgentOptions: () => api.get('/reports/collection-sheet/agent-options'),
  collectionSheetSetAgent: (payload) => api.put('/reports/collection-sheet/agent', payload),
  collectionSheetSetDate: (payload) => api.put('/reports/collection-sheet/date', payload),
  collectionSheetSetOfficePermanent: (payload) => api.put('/reports/collection-sheet/office-permanent', payload),
};

// ─── Users ─────────────────────────────────────────────────────────────────────
export const userAPI = {
  getAll: () => api.get('/users'),
  create: (data) => api.post('/users', data),
  update: (id, data) => api.put(`/users/${id}`, data),
  delete: (id) => api.delete(`/users/${id}`),
  activate: (id) => api.put(`/users/${id}/activate`),
  deletePermanent: (id) => api.delete(`/users/${id}/permanent`),
  toggleAttendance: (id) => api.put(`/users/${id}/toggle-attendance`),
  loginAs: (id) => api.post(`/users/${id}/login-as`),
  getAgentDetail: (id, month) => api.get(`/users/${id}/agent-detail`, { params: month ? { month } : {} }),
  updateAreas: (id, areas) => api.put(`/users/${id}/areas`, { areas }),
  updateSiteRole: (id, roleId) => api.put(`/users/${id}/site-role`, { roleId }),
  updateRole: (id, role) => api.put(`/users/${id}/role`, { role }),
  updateSecondaryRoles: (id, secondaryRoles) => api.put(`/users/${id}/secondary-roles`, { secondaryRoles }),
  updateFieldRights: (id, rights) => api.put(`/users/${id}/field-rights`, { rights }),
  getModuleRegistry: () => api.get('/users/module-registry'),
};

// ─── Site Roles (reusable rights templates) ────────────────────────────────────
export const roleAPI = {
  getAll: () => api.get('/roles'),
  create: (data) => api.post('/roles', data),
  update: (id, data) => api.put(`/roles/${id}`, data),
  delete: (id) => api.delete(`/roles/${id}`),
};

// ─── Agent App (mobile, cable-TV-only collection interface) ────────────────────
export const agentAppAPI = {
  getCustomers: (params) => api.get('/agent/customers', { params }),
  getCustomer: (id) => api.get(`/agent/customers/${id}`),
  getLedger: (id) => api.get(`/agent/customers/${id}/ledger`),
  deleteLedgerBill: (id, billId) => api.delete(`/agent/customers/${id}/ledger/bills/${billId}`),
  deleteLedgerPayment: (id, paymentId) => api.delete(`/agent/customers/${id}/ledger/payments/${paymentId}`),
  deleteLedgerAttempt: (id, attemptId) => api.delete(`/agent/customers/${id}/ledger/attempts/${attemptId}`),
  deleteLedgerAdjustment: (id, changeLogId) => api.delete(`/agent/customers/${id}/ledger/adjustments/${changeLogId}`),
  updateCustomer: (id, data) => api.put(`/agent/customers/${id}`, data),
  addCustomer: (data) => api.post('/agent/customers', data),
  addLead: (data) => api.post('/agent/leads', data),
  collectPayment: (id, data) => api.post(`/agent/customers/${id}/payment`, data),
  collectBalance: (id, data) => api.post(`/agent/customers/${id}/collect-balance`, data),
  markCollectionPending: (id, data) => api.post(`/agent/customers/${id}/collection-pending`, data),
  getCollectionPendingReasons: () => api.get('/agent/collection-pending-reasons'),
  getStbModels: () => api.get('/agent/stb-models'),
  getEntityCodes: () => api.get('/agent/entity-codes'),
  getAreas: () => api.get('/agent/areas'),
  adjustBalance: (id, data) => api.post(`/agent/customers/${id}/adjust-balance`, data),
  retrackStb: (id) => api.post(`/agent/customers/${id}/retrack-stb`, {}, { timeout: 30000 }),
  getPayment: (paymentId) => api.get(`/agent/payments/${paymentId}`),
  getReceiptUrl: (paymentId) => `${API_URL}/agent/payments/${paymentId}/receipt`,
  getReceiptBlob: (paymentId) => api.get(`/agent/payments/${paymentId}/receipt`, { responseType: 'blob' }),
  getReceiptJpegBlob: (paymentId) => api.get(`/agent/payments/${paymentId}/receipt.jpg`, { responseType: 'blob' }),
  settlePayment: (paymentId, data) => api.post(`/agent/payments/${paymentId}/collect`, data),
  shareBillWhatsApp: (paymentId) => api.post(`/agent/payments/${paymentId}/share-whatsapp`),
  getSummary: () => api.get('/agent/summary'),
  getCustomerCounts: (params) => api.get('/agent/customer-counts', { params }),
  sendWhatsAppMessage: (id, message, to) => api.post(`/agent/customers/${id}/send-message`, { message, to }),
  sendWhatsAppTemplate: (id, templateId, context) => api.post(`/agent/customers/${id}/send-message`, { templateId, context }),
  sendSMS: (id, message) => api.post(`/agent/customers/${id}/send-message`, { message, channel: 'sms' }),
  getMessageTemplates: () => api.get('/agent/message-templates'),
};

// ─── Money Manager ─────────────────────────────────────────────────────────────
export const moneyAPI = {
  getSummary: (period, monthOffset) => api.get('/money/summary', { params: { period, monthOffset } }),
  getTransactions: (params) => api.get('/money/transactions', { params }),
  createTransaction: (data) => api.post('/money/transactions', data, data instanceof FormData ? { headers: { 'Content-Type': undefined } } : undefined),
  updateTransaction: (id, data) => api.put(`/money/transactions/${id}`, data, data instanceof FormData ? { headers: { 'Content-Type': undefined } } : undefined),
  deleteTransaction: (id) => api.delete(`/money/transactions/${id}`),
};

// ─── Settings ──────────────────────────────────────────────────────────────────
export const settingsAPI = {
  getOLT:          ()     => api.get('/settings/olt'),
  saveOLT:         (data) => api.put('/settings/olt', data),
  testOLTConnection: ()   => api.post('/settings/olt/test'),
  getOLTV2:          ()     => api.get('/settings/olt-v2'),
  saveOLTV2:         (data) => api.put('/settings/olt-v2', data),
  // Backend-side timeouts (10s Telnet/SSH login, 8s SNMP) can each run close to the global
  // 15s default — give these a bit of headroom so a slow-but-real backend error/success
  // reaches the UI instead of being cut off by a client-side timeout first.
  testOLTV2Telnet:   ()     => api.post('/settings/olt-v2/test-telnet', {}, { timeout: 20000 }),
  testOLTV2Ssh:      ()     => api.post('/settings/olt-v2/test-ssh',    {}, { timeout: 20000 }),
  testOLTV2Snmp:     ()     => api.post('/settings/olt-v2/test-snmp',   {}, { timeout: 20000 }),
  getBilling:      ()     => api.get('/settings/billing'),
  saveBilling:     (data) => api.put('/settings/billing', data),
  getReceiptNumbering:  ()     => api.get('/settings/receipt-numbering'),
  saveReceiptNumbering: (data) => api.put('/settings/receipt-numbering', data),
  getCompany:      ()     => api.get('/settings/company'),
  // Logo uploads make this payload much bigger than other settings saves and, on a slow
  // connection, can take longer than the default 15s timeout — give it more room rather
  // than surfacing a misleading generic "Save failed" for what's really just a timeout.
  saveCompany:     (data) => api.put('/settings/company', data, { timeout: 60000 }),
  // NAS
  getNASList:         ()          => api.get('/settings/nas'),
  addNAS:             (data)      => api.post('/settings/nas', data),
  updateNAS:          (id, data)  => api.put(`/settings/nas/${id}`, data),
  deleteNAS:          (id)        => api.delete(`/settings/nas/${id}`),
  testNAS:            (id)        => api.post(`/settings/nas/${id}/test`),
  getNASInterfaces:   (id)        => api.get(`/settings/nas/${id}/interfaces`),
  // WhatsApp API
  getWhatsApp:        ()          => api.get('/settings/whatsapp'),
  saveWhatsApp:       (data)      => api.put('/settings/whatsapp', data),
  testWhatsApp:       ()          => api.post('/settings/whatsapp/test'),
  // Payment Gateway
  getPaymentGateway:  (service)   => api.get(`/settings/payment-gateway/${service}`),
  savePaymentGateway: (service, data) => api.put(`/settings/payment-gateway/${service}`, data),
  testPaymentGateway: (service)   => api.post(`/settings/payment-gateway/${service}/test`),
  // Named profiles + rules (2026-07-28); the same GET also carries each service's own
  // gateway choice, which the Online PG tabs render as a dropdown.
  getGatewayProfiles: ()          => api.get('/settings/payment-gateway-profiles'),
  saveGatewayChoice:  (service, data) => api.put(`/settings/payment-gateway/${service}/routing`, data),
  // Broadband UPI QR entries
  getBroadbandUpi:    ()          => api.get('/settings/broadband-upi'),
  saveBroadbandUpi:   (data)      => api.put('/settings/broadband-upi', data),
  getCatvUpi:         ()          => api.get('/settings/catv-upi'),
  saveCatvUpi:        (data)      => api.put('/settings/catv-upi', data),
  // Netzur Integration
  getImbs:            ()          => api.get('/settings/imbs'),
  saveImbs:           (data)      => api.put('/settings/imbs', data),
  testImbs:           ()          => api.post('/settings/imbs/test'),
  testImbsPanel:      ()          => api.post('/settings/imbs/panel-test', {}, { timeout: 60000 }),
  // PlayBoxTV Integration
  getPlayboxtv:       ()          => api.get('/settings/playboxtv'),
  savePlayboxtv:      (data)      => api.put('/settings/playboxtv', data),
  // Google Sheets Sync
  getGoogleSheets:    ()          => api.get('/settings/google-sheets'),
  saveGoogleSheets:   (data)      => api.put('/settings/google-sheets', data),
  testGoogleSheets:   ()          => api.post('/settings/google-sheets/test'),
  testPlayboxtv:      ()          => api.post('/settings/playboxtv/test'),
  // App View (tablet/mobile bottom-tab + More-menu layout)
  getAppView:         ()          => api.get('/settings/app-view'),
  saveAppView:        (data)      => api.put('/settings/app-view', data),
  // Dashboard Widgets (optional widgets removed from the default view, opt back in)
  getDashboardWidgets:  ()        => api.get('/settings/dashboard-widgets'),
  saveDashboardWidgets: (data)    => api.put('/settings/dashboard-widgets', data),
};

// ─── NAS Live Bandwidth ────────────────────────────────────────────────────────
export const nasAPI = {
  getPollers:         ()                   => api.get('/nas/pollers'),
  getAllLive:         (nasId)              => api.get('/nas/live', { params: nasId ? { nasId } : {} }),
  getSubscriberLive: (username)           => api.get(`/nas/live/${encodeURIComponent(username)}`),
  getInterfaces:     (nasId)              => api.get('/nas/interfaces', { params: nasId ? { nasId } : {} }),
  getBandwidth:      (nasId, iface)       => api.get('/nas/bandwidth', { params: { ...(nasId ? { nasId } : {}), ...(iface ? { iface } : {}) } }),
  getUplinkBandwidth: ()                  => api.get('/nas/uplink-bandwidth'),
  getOnlineSubscribers: ()                => api.get('/nas/online-subscribers'),
  reloadNAS:         (nasId)              => api.post(`/nas/active/${nasId}`),
  getUsageSummary:   (username)           => api.get(`/nas/usage/${encodeURIComponent(username)}/summary`),
  getUsageSessions:  (username, limit)    => api.get(`/nas/usage/${encodeURIComponent(username)}/sessions`, { params: limit ? { limit } : {} }),
  kickSession:       (username)           => api.post('/nas/kick', { username }),
};

// ─── Technical (network-hardware login: connections roster) ───────────────────
export const technicalAPI = {
  getConnections: (params) => api.get('/technical/connections', { params }),
  getSummary:     (params) => api.get('/technical/summary', { params }),
  getDashboard:   ()       => api.get('/technical/dashboard', { timeout: 30000 }),
  getBandwidth:   ()       => api.get('/technical/bandwidth'),
  getLiveRates:   ()       => api.get('/technical/live-rates'),
  getPowerThresholds: ()   => api.get('/technical/power-thresholds'),
  getOnuCustomer: (serial) => api.get(`/technical/onu-customer/${encodeURIComponent(serial)}`),
  getPonSummary:  ()       => api.get('/technical/pon-summary', { timeout: 30000 }),
  getAlarms:      (params) => api.get('/technical/alarms', { params }),
  acknowledgeAlarm:    (id) => api.post(`/technical/alarms/${id}/acknowledge`),
  acknowledgeAllAlarms: ()  => api.post('/technical/alarms/acknowledge-all'),
};

// OLT calls can take 30-60s (scraping 16 PON ports from real hardware)
const OLT_TIMEOUT = 90000;

// ─── OLT ───────────────────────────────────────────────────────────────────────
export const oltAPI = {
  getStatus:        ()                         => api.get('/olt/status',                       { timeout: OLT_TIMEOUT }),
  getONUs:          (params)                   => api.get('/olt/onus',         { params,        timeout: OLT_TIMEOUT }),
  getGEPorts:       ()                         => api.get('/olt/ge-ports',                     { timeout: OLT_TIMEOUT }),
  getONUDetail:     (slot, port, onuId)        => api.get(`/olt/onus/${slot}/${port}/${onuId}`,{ timeout: OLT_TIMEOUT }),
  getPorts:         ()                         => api.get('/olt/ports',                        { timeout: OLT_TIMEOUT }),
  getAlarms:        (params)                   => api.get('/olt/alarms',       { params,        timeout: OLT_TIMEOUT }),
  provision:        (data)                     => api.post('/olt/onus/provision', data,         { timeout: OLT_TIMEOUT }),
  rebootONU:        (slot, port, onuId)        => api.post(`/olt/onus/${slot}/${port}/${onuId}/reboot`,  {},              { timeout: OLT_TIMEOUT }),
  upgradeONU:       (slot, port, onuId, body)  => api.post(`/olt/onus/${slot}/${port}/${onuId}/upgrade`, body || {},      { timeout: OLT_TIMEOUT }),
  setONUState:      (slot, port, onuId, state) => api.put(`/olt/onus/${slot}/${port}/${onuId}/state`, { state }, { timeout: OLT_TIMEOUT }),
  deregisterONU:    (slot, port, onuId)        => api.delete(`/olt/onus/${slot}/${port}/${onuId}`,     { timeout: OLT_TIMEOUT }),
  saveONULabel:     (onuId, label)             => api.put('/olt/onus/label',   { onuId, label }),
  getSerialMap:     ()                         => api.get('/olt/onus/serial-map'),
  matchByUsername:  (username)                 => api.get('/olt/match-by-username', { params: { username }, timeout: OLT_TIMEOUT }),
  fetchAllBroadbandOlt: ()                     => api.post('/olt/broadband/fetch-all', {}, { timeout: OLT_TIMEOUT }),
  fetchAllBroadbandOltMac: ()                  => api.post('/olt/broadband/fetch-all-mac', {}, { timeout: OLT_TIMEOUT }),
  applyBlankOnuMacMatch:   (onuIds)            => api.post('/olt/broadband/fetch-all-mac/apply', { onuIds }),
  fetchAllBroadbandOltMacStatus: ()            => api.get('/olt/broadband/fetch-all-mac/status'),
  changeONUProfile: (port, onuId, body)        => api.put(`/olt/onus/${port}/${onuId}/profile`, body, { timeout: OLT_TIMEOUT }),
  getONUProfile:    (port, onuId)              => api.get(`/olt/onus/${port}/${onuId}/profile`, { timeout: OLT_TIMEOUT }),
};

// ─── OLT Info V2.0 — live actions (admin-only, independent from oltAPI above; every call
// is refused server-side unless "Enable live actions" has been saved ON — see
// backend/src/services/oltServiceV2.js assertEnabled()) ─────────────────────────
export const oltV2API = {
  runCommand:       (cmd)                      => api.post('/olt-v2/cli/run', { cmd },              { timeout: OLT_TIMEOUT }),
  sendConfig:       (commands)                 => api.post('/olt-v2/cli/config', { commands },       { timeout: OLT_TIMEOUT }),
  getONUs:          ()                         => api.get('/olt-v2/onus',                            { timeout: OLT_TIMEOUT }),
  getONUDetail:     (port, onuId)              => api.get(`/olt-v2/onus/${port}/${onuId}`,           { timeout: OLT_TIMEOUT }),
  getPorts:         ()                         => api.get('/olt-v2/ports',                           { timeout: OLT_TIMEOUT }),
  getAlarms:        ()                         => api.get('/olt-v2/alarms',                          { timeout: OLT_TIMEOUT }),
  getGEPorts:       ()                         => api.get('/olt-v2/ge-ports',                        { timeout: OLT_TIMEOUT }),
  provisionONU:     (data)                     => api.post('/olt-v2/onus/provision', data,           { timeout: OLT_TIMEOUT }),
  deregisterONU:    (port, onuId)              => api.delete(`/olt-v2/onus/${port}/${onuId}`,        { timeout: OLT_TIMEOUT }),
  rebootONU:        (port, onuId)              => api.post(`/olt-v2/onus/${port}/${onuId}/reboot`, {}, { timeout: OLT_TIMEOUT }),
  upgradeONU:       (port, onuId, firmwareVersion) => api.post(`/olt-v2/onus/${port}/${onuId}/upgrade`, { firmwareVersion }, { timeout: OLT_TIMEOUT }),
  setONUAdminState: (port, onuId, state)       => api.put(`/olt-v2/onus/${port}/${onuId}/state`, { state }, { timeout: OLT_TIMEOUT }),
  saveLabel:        (port, onuId, label)       => api.put('/olt-v2/onus/label', { port, onuId, label }, { timeout: OLT_TIMEOUT }),
  changeONUProfile: (port, onuId, lineProfile, serviceProfile) => api.put(`/olt-v2/onus/${port}/${onuId}/profile`, { lineProfile, serviceProfile }, { timeout: OLT_TIMEOUT }),
};

// ─── Testing (admin-only reboot-debugging panel) ────────────────────────────────
// Isolated per-step triggers for "Fetch All Broadband OLT Data" — each one calls the real
// backend logic for just that step, so an admin can run them one at a time and watch for the
// OLT to reboot instead of only ever being able to fire the whole bulk action at once.
export const testingAPI = {
  oltStep1OnuList:        () => api.post('/testing/olt/step1-onu-list',        {}, { timeout: OLT_TIMEOUT }),
  oltStep1aPorts:          ()            => api.get('/testing/olt/step1a-ports', { timeout: OLT_TIMEOUT }),
  oltStep1bStatus:         (port)        => api.post('/testing/olt/step1b-status', { port }, { timeout: OLT_TIMEOUT }),
  oltStep1cOptical:        (port)        => api.post('/testing/olt/step1c-optical', { port }, { timeout: OLT_TIMEOUT }),
  oltStep1dAuth:           (port)        => api.post('/testing/olt/step1d-auth', { port }, { timeout: OLT_TIMEOUT }),
  oltStep2MatchUsernames: () => api.post('/testing/olt/step2-match-usernames', {}, { timeout: OLT_TIMEOUT }),
  oltStep3WriteDb:        () => api.post('/testing/olt/step3-write-db',        {}, { timeout: OLT_TIMEOUT }),
  oltStep4MacFallback:    () => api.post('/testing/olt/step4-mac-fallback',    {}, { timeout: OLT_TIMEOUT }),
  oltStep4aUnresolvedList: ()             => api.get('/testing/olt/step4a-unresolved-list', { timeout: OLT_TIMEOUT }),
  oltStep4Single:          (customerId)   => api.post('/testing/olt/step4-single', { customerId }, { timeout: OLT_TIMEOUT }),
  oltStep4bResolveMac:     (customerId)   => api.post('/testing/olt/step4b-resolve-mac', { customerId }, { timeout: OLT_TIMEOUT }),
  oltStep4cMacLookup:      (mac)          => api.post('/testing/olt/step4c-mac-lookup', { mac }, { timeout: OLT_TIMEOUT }),
  oltStep4dWriteLabel:     (customerId, onuId) => api.post('/testing/olt/step4d-write-label', { customerId, onuId }, { timeout: OLT_TIMEOUT }),
  oltStep5PauseResumeAlarm: () => api.post('/testing/olt/step5-pause-resume-alarm', {}, { timeout: OLT_TIMEOUT }),
  oltStep5aPauseAlarm:     ()  => api.post('/testing/olt/step5a-pause-alarm', {}, { timeout: OLT_TIMEOUT }),
  oltStep5bResumeAlarm:    ()  => api.post('/testing/olt/step5b-resume-alarm', {}, { timeout: OLT_TIMEOUT }),
  oltStep6Summary:        () => api.get('/testing/olt/step6-summary',         { timeout: OLT_TIMEOUT }),
};

// ─── WhatsApp API (self-hosted Baileys gateway, standalone option alongside Deropo) ───
export const whatsappAPI = {
  getStatus:      ()               => api.get('/whatsapp/status'),
  getRecent:      (limit)          => api.get('/whatsapp/messages/recent', { params: limit ? { limit } : {} }),
  connect:        ()               => api.post('/whatsapp/connect'),
  logout:         ()               => api.post('/whatsapp/logout'),
  sendTest:       (to, message)    => api.post('/whatsapp/send-test', { to, message }),
  sendButtonsTest: (data)          => api.post('/whatsapp/send-buttons-test', data),
  getQueueStatus: ()               => api.get('/whatsapp/queue-status'),
  getPendingQueue: ()              => api.get('/whatsapp/pending-queue'),
  savePacing:     (data)           => api.put('/whatsapp/pacing', data),
  // History import (throwaway second linked device, to pull in pre-existing chats)
  startHistoryImport:  ()          => api.post('/whatsapp/history-import/start'),
  getHistoryImport:    ()          => api.get('/whatsapp/history-import/status'),
  stopHistoryImport:   ()          => api.post('/whatsapp/history-import/stop'),
  // Chatbot automation
  getAutomationSettings: ()        => api.get('/whatsapp/automation/settings'),
  saveAutomationSettings: (data)   => api.put('/whatsapp/automation/settings', data),
  getAutomationRules: ()           => api.get('/whatsapp/automation/rules'),
  createAutomationRule: (data)     => api.post('/whatsapp/automation/rules', data),
  updateAutomationRule: (id, data) => api.put(`/whatsapp/automation/rules/${id}`, data),
  deleteAutomationRule: (id)       => api.delete(`/whatsapp/automation/rules/${id}`),
  // Deropo Templates (editable reference library)
  getDeropoTemplates: ()           => api.get('/whatsapp/deropo-templates'),
  createDeropoTemplate: (data)     => api.post('/whatsapp/deropo-templates', data),
  updateDeropoTemplate: (id, data) => api.put(`/whatsapp/deropo-templates/${id}`, data),
  deleteDeropoTemplate: (id)       => api.delete(`/whatsapp/deropo-templates/${id}`),
};

// ─── WhatsApp Inbox (web-WhatsApp-style chat page over the gateway) ───
export const whatsappInboxAPI = {
  getChats:     (params)          => api.get('/whatsapp-inbox/chats', { params }),
  getMessages:  (chatId, params)  => api.get(`/whatsapp-inbox/chats/${encodeURIComponent(chatId)}/messages`, { params }),
  getCustomers: (chatId)          => api.get(`/whatsapp-inbox/chats/${encodeURIComponent(chatId)}/customers`),
  loadOlder:    (chatId)          => api.post(`/whatsapp-inbox/chats/${encodeURIComponent(chatId)}/load-older`),
  markRead:     (chatId)          => api.post(`/whatsapp-inbox/chats/${encodeURIComponent(chatId)}/read`),
  sendText:     (chatId, text)    => api.post(`/whatsapp-inbox/chats/${encodeURIComponent(chatId)}/send-text`, { text }),
  sendMedia:    (chatId, file, caption) => {
    const fd = new FormData();
    fd.append('file', file);
    if (caption) fd.append('caption', caption);
    return api.post(`/whatsapp-inbox/chats/${encodeURIComponent(chatId)}/send-media`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  // Authenticated media can't be a plain <img src> (auth is header-only) — fetch as a blob.
  getMediaBlob: (messageId)       => api.get(`/whatsapp-inbox/media/${encodeURIComponent(messageId)}`, { responseType: 'blob' }),
  // Starting a conversation with someone who never messaged first
  searchCustomers: (q)            => api.get('/whatsapp-inbox/customers/search', { params: { q } }),
  openChat:     (payload)         => api.post('/whatsapp-inbox/chats/open', payload),
  // Quick actions
  getTemplates: ()                => api.get('/whatsapp-inbox/templates'),
  sendTemplate: (chatId, data)    => api.post(`/whatsapp-inbox/chats/${encodeURIComponent(chatId)}/send-template`, data),
  sendPaymentLink: (chatId, data) => api.post(`/whatsapp-inbox/chats/${encodeURIComponent(chatId)}/send-payment-link`, data),
  // Shared-inbox workflow
  getStaff:     ()                => api.get('/whatsapp-inbox/staff'),
  getMeta:      (chatId)          => api.get(`/whatsapp-inbox/chats/${encodeURIComponent(chatId)}/meta`),
  saveMeta:     (chatId, data)    => api.put(`/whatsapp-inbox/chats/${encodeURIComponent(chatId)}/meta`, data),
  addNote:      (chatId, text)    => api.post(`/whatsapp-inbox/chats/${encodeURIComponent(chatId)}/notes`, { text }),
  getUnreadCount: ()              => api.get('/whatsapp-inbox/unread-count'),
  getLive:      (customerId)      => api.get(`/whatsapp-inbox/customers/${customerId}/live`),
};

// Live inbox stream (server-sent events). Not an axios call: EventSource can't send an
// Authorization header, so this reads the response body as a stream from fetch instead.
// Returns a stop() function. onEvent gets each parsed payload; onError signals a dropped
// connection so the caller can fall back to its slower polling until it reconnects.
// `path` lets the Kapso inbox use the same reader for /kapso/stream.
export function openInboxStream({ onEvent, onOpen, onError, path = '/whatsapp-inbox/stream' }) {
  const controller = new AbortController();
  let stopped = false;
  (async () => {
    while (!stopped) {
      try {
        const res = await fetch(`${API_URL}${path}`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
          signal: controller.signal,
        });
        if (!res.ok || !res.body) throw new Error(`stream failed (${res.status})`);
        onOpen?.(); // connected — the stream can sit idle for a long time before any event arrives
        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        for (;;) {
          const { value, done } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          const parts = buffer.split('\n\n');
          buffer = parts.pop() || '';
          for (const part of parts) {
            const line = part.split('\n').find(l => l.startsWith('data:'));
            if (!line) continue; // ': ping' keep-alives carry no data
            try { onEvent(JSON.parse(line.slice(5).trim())); } catch { /* ignore a malformed frame */ }
          }
        }
        throw new Error('stream ended');
      } catch (err) {
        if (stopped || controller.signal.aborted) return;
        onError?.(err);
        await new Promise(r => setTimeout(r, 5000)); // reconnect, e.g. after an API restart
      }
    }
  })();
  return () => { stopped = true; controller.abort(); };
}

// Customer Hardware History — RX power graph, ONUs had, hardware events/outages
export const hardwareHistoryAPI = {
  getCustomer: (customerId, range) => api.get(`/hardware-history/customer/${customerId}`, { params: { range } }),
  getUptime:   (customerId, params) => api.get(`/hardware-history/customer/${customerId}/uptime`, { params }),
};

// Admin-only merged OLT activity feed (Alarm status history + OLT admin-action audit trail)
export const oltLogsAPI = {
  getFeed:   (params) => api.get('/olt-logs', { params }),
  getCounts: ()       => api.get('/olt-logs/counts'),
};

// ─── Messaging (WhatsApp / SMS broadcasts) ─────────────────────────────────────
// ─── Kapso (official WhatsApp Cloud API) — see backend/src/routes/kapso.js ───
export const kapsoAPI = {
  numbers:         ()                  => api.get('/kapso/numbers'),
  conversations:   (params)            => api.get('/kapso/conversations', { params }),
  messages:        (id, params)        => api.get(`/kapso/conversations/${id}/messages`, { params }),
  customers:       (phone)             => api.get(`/kapso/customers/${encodeURIComponent(phone)}`),
  markRead:        (id)                => api.post(`/kapso/conversations/${id}/read`),
  botResume:       (id)                => api.post(`/kapso/conversations/${id}/bot-resume`),
  bot:             ()                  => api.get('/kapso/bot'),
  saveBot:         (data)              => api.put('/kapso/bot', data),
  numberRoles:     ()                  => api.get('/kapso/number-roles'),
  saveNumberRoles: (data)              => api.put('/kapso/number-roles', data),
  accounts:        ()                  => api.get('/kapso/accounts'),
  saveAccount:     (account, data)     => api.put(`/kapso/accounts/${account}`, data),
  removeAccount:   (account)           => api.delete(`/kapso/accounts/${account}`),
  setupLink:       (account)           => api.post('/kapso/setup-link', { account }),
  send:            (data)              => api.post('/kapso/send', data),
  sendBulk:        (data)              => api.post('/kapso/send-bulk', data, { timeout: 600000 }),
  usage:           (pid)               => api.get(`/kapso/usage/${pid}`),
  templates:       (waba)              => api.get(`/kapso/templates/${waba}`),
  createTemplate:  (waba, data)        => api.post(`/kapso/templates/${waba}`, data),
  deleteTemplate:  (waba, name)        => api.delete(`/kapso/templates/${waba}/${encodeURIComponent(name)}`),
  broadcasts:      ()                  => api.get('/kapso/broadcasts'),
  broadcast:       (id)                => api.get(`/kapso/broadcasts/${id}`),
  createBroadcast: (data)              => api.post('/kapso/broadcasts', data),
  sendBroadcast:   (id)                => api.post(`/kapso/broadcasts/${id}/send`),
  contacts:        (params)            => api.get('/kapso/contacts', { params }),
  profile:         (pid)               => api.get(`/kapso/profile/${pid}`),
  saveProfile:     (pid, data)         => api.put(`/kapso/profile/${pid}`, data),
  blocked:         (pid)               => api.get(`/kapso/blocked/${pid}`),
  setBlocked:      (pid, phone, block) => api.post(`/kapso/blocked/${pid}`, { phone, block }),
};

export const messagingAPI = {
  getSmsSettings:   ()         => api.get('/messaging/sms-settings'),
  saveSmsSettings:  (data)     => api.put('/messaging/sms-settings', data),
  testSms:          (mobile)   => api.post('/messaging/sms-settings/test', { mobile }, { timeout: 30000 }),
  getTemplates:     ()         => api.get('/messaging/templates'),
  createTemplate:   (data)     => api.post('/messaging/templates', data),
  updateTemplate:   (id, data) => api.put(`/messaging/templates/${id}`, data),
  deleteTemplate:   (id)       => api.delete(`/messaging/templates/${id}`),
  // Content-Type must be explicitly unset (not omitted) — see adsAPI.upload's comment.
  uploadTemplateImage: (file) => { const fd = new FormData(); fd.append('image', file); return api.post('/messaging/templates/upload-image', fd, { headers: { 'Content-Type': undefined } }); },
  previewRecipients:(audience) => api.post('/messaging/recipients-preview', { audience }),
  exportRecipients: (audience) => api.post('/messaging/recipients-export', { audience }, { responseType: 'blob' }),
  send:             (data)     => api.post('/messaging/send', data, { timeout: 600000 }),
  getHistory:       (params)   => api.get('/messaging/history', { params }),
  getAlertRecipients:  ()           => api.get('/messaging/alert-recipients'),
  getSchedules:        ()           => api.get('/messaging/schedules'),
  saveSchedules:       (data)       => api.put('/messaging/schedules', data),
  getAlertGroups:      ()           => api.get('/messaging/alert-recipients/groups'),
  saveAlertRecipients: (recipients) => api.put('/messaging/alert-recipients', { recipients }),
  testAlertRecipient:  (r)          => api.post('/messaging/alert-recipients/test', r, { timeout: 60000 }),
  getAlertTemplates:   ()           => api.get('/messaging/alert-templates'),
  saveAlertTemplates:  (templates)  => api.put('/messaging/alert-templates', { templates }),
  previewPushOffer: (audience) => api.post('/messaging/push-offers/preview', { audience }),
  // Content-Type must be explicitly unset (not omitted) — see adsAPI.upload's comment.
  sendPushOffer: (formData) => api.post('/messaging/push-offers', formData, { headers: { 'Content-Type': undefined }, timeout: 120000 }),
};

// ─── GTPL ──────────────────────────────────────────────────────────────────────
export const gtplAPI = {
  retrack: (stb) => api.post('/gtpl/retrack', { stb }, { timeout: 30000 }),
};

// ─── Netzur ─────────────────────────────────────────────────────────────
export const imbsAPI = {
  getStatus:           ()                           => api.get('/imbs/status'),
  getPackages:         ()                           => api.get('/imbs/packages'),
  importPackages:      ()                           => api.post('/imbs/packages/import'),
  importSubscribers:   (fields)                      => api.post('/imbs/subscribers/import', { fields }),
  getSubscribers:      (params)                     => api.get('/imbs/subscribers', { params }),
  getStatusMap:        ()                           => api.get('/imbs/subscriber-status-map'),
  getSubscriber:       (id)                         => api.get(`/imbs/subscribers/${id}`),
  linkCustomer:        (customerId, imbsId)         => api.post(`/imbs/customers/${customerId}/link`, { imbsSubscriberId: imbsId }),
  createSubscriber:    (customerId, data)           => api.post(`/imbs/customers/${customerId}/create-subscriber`, data),
  unlinkCustomer:      (customerId)                 => api.delete(`/imbs/customers/${customerId}/link`),
  resetMac:            (customerId)                  => api.post(`/imbs/customers/${customerId}/reset-mac`),
  pushPayment:         (customerId, data)           => api.post(`/imbs/customers/${customerId}/push-payment`, data),
  recharge:            (customerId, data)           => api.post(`/imbs/customers/${customerId}/recharge`, data),
  changeExpiry:        (customerId, expiryDate)     => api.put(`/imbs/customers/${customerId}/expiry`, { expiryDate }),
  getSessions:         (customerId)                 => api.get(`/imbs/customers/${customerId}/sessions`),
  getInvoices:         (customerId)                 => api.get(`/imbs/customers/${customerId}/invoices`),
  pullAddress:         (customerId)                 => api.post(`/imbs/customers/${customerId}/pull-address`),
  pullAddressBulk:     ()                            => api.post('/imbs/customers/pull-address/bulk', {}, { timeout: 60000 }),
  syncStatusBulk:      ()                            => api.post('/imbs/customers/sync-status/bulk', {}, { timeout: 60000 }),
  setMacCheck:         (customerId, macCheck)       => api.put(`/imbs/customers/${customerId}/mac-check`, { macCheck }),
  setAutoRenew:        (customerId, enabled, cycles) => api.put(`/imbs/customers/${customerId}/auto-renew`, { enabled, cycles }),
  setStatus:           (customerId, isActive)         => api.put(`/imbs/customers/${customerId}/status`, { isActive }),
  kickSession:         (customerId)                    => api.post(`/imbs/customers/${customerId}/kick-session`),
  getKyc:              (customerId)                    => api.get(`/imbs/customers/${customerId}/kyc`),
  getKycPanelLink:     (customerId)                    => api.get(`/imbs/customers/${customerId}/kyc/panel-link`, { timeout: 60000 }),
  pushKycDocuments:    (customerId)                    => api.post(`/imbs/customers/${customerId}/kyc/push-documents`, {}, { timeout: 90000 }),
};

export const gtplSaathiAPI = {
  getSessionStatus: ()                => api.get('/gtplSaathi/session'),
  getEntityWallets:  ()                => api.get('/gtplSaathi/entity-wallets'),
  refreshEntityWallets: ()             => api.post('/gtplSaathi/entity-wallets/refresh', {}, { timeout: 120000 }),
  saveSession:       (cookie)         => api.post('/gtplSaathi/session', { cookie }),
  testSession:       ()                => api.post('/gtplSaathi/session/test'),
  saveAutoLogin:     (body)            => api.put('/gtplSaathi/auto-login', body),
  loginNow:          ()                => api.post('/gtplSaathi/auto-login/test', {}, { timeout: 180000 }),
  setNavVisibility:  (visible)        => api.put('/gtplSaathi/nav-visibility', { visible }),
  getDisconnectAutomation: ()          => api.get('/gtplSaathi/disconnect-automation'),
  setDisconnectAutomation: (enabled)  => api.put('/gtplSaathi/disconnect-automation', { enabled }),
  getRenewalAastha: ()                => api.get('/gtplSaathi/renewal-aastha'),
  setRenewalAastha: (enabled)         => api.put('/gtplSaathi/renewal-aastha', { enabled }),
  getLongTermAutoStart: ()            => api.get('/gtplSaathi/long-term/auto-start'),
  setLongTermAutoStart: (enabled)     => api.put('/gtplSaathi/long-term/auto-start', { enabled }),
  // Retry-queue log — every write action (ticket/renew/multi-renew/suspend/order/modify/
  // hardware-replacement) that got queued because the session had expired at the time,
  // auto-replayed the moment a fresh cookie is saved (see POST /session above).
  getPendingTasks:   (params)          => api.get('/gtplSaathi/pending-tasks', { params }),
  // Manually re-attempts one queued/failed task right now — {} replays it unchanged, or
  // { actionType: 'orderPlace', payload } to retry it as a real Place Order instead (see
  // routes/gtplSaathi.js's pending-tasks/:id/retry comment for why that's a distinct case,
  // not something this can just auto-decide).
  retryPendingTask:  (id, body = {})   => api.post(`/gtplSaathi/pending-tasks/${id}/retry`, body, { timeout: 60000 }),
  // Full audit trail of every real GTPL Saathi write action (success AND failure), manual
  // or auto-triggered off a payment — distinct from the retry queue above, which only ever
  // captures a session-expiry failure, never a real one like "Insufficient Wallet Balance".
  getActionLogs:     (params)          => api.get('/gtplSaathi/logs', { params }),
  // Local per-customer ticket log (any GTPL ticket type raised through this CRM for that
  // customer's account, not just disconnect requests) — see routes/gtplSaathi.js's
  // customer-tickets routes for why this can't be a live GTPL query.
  listCustomerTickets:     (customerId) => api.get('/gtplSaathi/customer-tickets', { params: { customerId } }),
  // mode 'instant' (default) drives the same real headless-browser ticket submission as
  // raiseSupportTicket below (20-35s typical) — same generous timeout. mode 'schedule' only
  // applies to complaintType '382' (disconnect request). Pass a FormData (with a 'photo' file
  // part) to attach a photo, same pattern as raiseSupportTicket below.
  createCustomerTicket:    (params)    => api.post('/gtplSaathi/customer-tickets', params, { timeout: 60000, ...(params instanceof FormData ? { headers: { 'Content-Type': undefined } } : {}) }),
  cancelCustomerTicket:    (ticketId)  => api.delete(`/gtplSaathi/customer-tickets/${ticketId}`),
  search:            (params)         => api.post('/gtplSaathi/search', params),
  renewSearch:       (params)         => api.post('/gtplSaathi/renew/search', params),
  renew:             (params)         => api.post('/gtplSaathi/renew', params),
  suspendActive:     (params)         => api.post('/gtplSaathi/suspend', params),
  getSuspendReasons: ()                => api.get('/gtplSaathi/suspend-reasons'),
  getSupportTicketOptions: ()          => api.get('/gtplSaathi/support-ticket-options'),
  // params may be a plain object (text-only ticket) or a FormData (photo attached) — same
  // Content-Type-undefined pattern as complaintsAPI/notesAPI above, so axios/the browser set
  // the multipart boundary itself instead of the shared instance's default JSON header.
  // Custom timeout (default is 15s) — this endpoint drives a real headless browser through
  // GTPL's own multi-step page server-side (see gtplSaathiService.raiseSupportTicket), which
  // routinely takes 20-35s. Confirmed live (2026-08-26): the default timeout fired and showed
  // "Failed to raise ticket" while the backend kept running and created a REAL ticket on
  // GTPL's side seconds later — same pattern as syncMyDeviceStatus/retrackStb above.
  raiseSupportTicket:      (params)    => api.post('/gtplSaathi/support-ticket', params, { timeout: 60000, ...(params instanceof FormData ? { headers: { 'Content-Type': undefined } } : {}) }),
  // My Tickets — MyTicket.aspx is a single huge (~16MB) unpaginated page, so both of these
  // are slow relative to most calls here; generous timeouts on purpose (see
  // gtplSaathiService.listTickets/getTicketDetail).
  listTickets:        (reqId, limit) => api.get('/gtplSaathi/tickets', { params: { reqId: reqId || undefined, limit }, timeout: 45000 }),
  getTicketDetail:    (reqId)        => api.get(`/gtplSaathi/tickets/${encodeURIComponent(reqId)}`, { timeout: 90000 }),
  // Order — a genuinely NEW package order (real money, distinct from renew above). All four
  // drive a real headless browser through GTPL's multi-step Order wizard server-side (see
  // gtplSaathiService.js), same 60s-timeout reasoning as raiseSupportTicket above.
  orderSearch:        (params) => api.get('/gtplSaathi/order/search', { params, timeout: 60000 }),
  orderChannelSearch: (params) => api.get('/gtplSaathi/order/channels', { params, timeout: 60000 }),
  orderPreview:       (params) => api.post('/gtplSaathi/order/preview', params, { timeout: 60000 }),
  placeGtplOrder:     (params) => api.post('/gtplSaathi/order/place', params, { timeout: 60000 }),
  // Multi Renew — loops the single Renew flow per item server-side (see
  // gtplSaathiService.multiRenew), so this can take a while for a long list.
  multiRenew:         (items, validity) => api.post('/gtplSaathi/multi-renew', { items, validity }, { timeout: 120000 }),
  // Multi Renew — entity-wise browser (mirrors gtplsaathi.com's own MultiRenew.aspx screen).
  multiRenewEntities: () => api.get('/gtplSaathi/multi-renew/entities'),
  multiRenewDates:    (filterType) => api.get('/gtplSaathi/multi-renew/dates', { params: { filterType } }),
  multiRenewSearch:   (params) => api.post('/gtplSaathi/multi-renew/search', params),
  multiRenewCalculateAmount: (params) => api.post('/gtplSaathi/multi-renew/calculate-amount', params, { timeout: 60000 }),
  multiRenewSubmit:   (params) => api.post('/gtplSaathi/multi-renew/submit', params, { timeout: 60000 }),
  // Modify — edits a subscriber's profile fields on Modify.aspx.
  modifySearch:       (accountNumber) => api.post('/gtplSaathi/modify/search', { accountNumber }),
  // Our own CRM record for this GTPL account — the "Fetch from CRM" button's source of truth.
  modifyCrmLookup:    (accountNumber) => api.get('/gtplSaathi/modify/crm-lookup', { params: { accountNumber } }),
  // 60s timeout — a successful save also raises a real eSupport ticket server-side (see
  // routes/gtplSaathi.js's POST /modify), same headless-browser cost as raiseSupportTicket above.
  modifyProfile:      (accountNumber, fields) => api.post('/gtplSaathi/modify', { accountNumber, fields }, { timeout: 60000 }),
  // Hardware Replacement — search is plain HTTP (fast); the actual submit drives a headless
  // browser server-side (see gtplSaathiService.hardwareReplacement), same 60s timeout as Order.
  hardwareReplacementSearch: (params) => api.get('/gtplSaathi/hardware-replacement/search', { params }),
  hardwareReplacement:       (params) => api.post('/gtplSaathi/hardware-replacement', params, { timeout: 60000 }),
  // 6/12-month box swap & order — preview runs in the background server-side; poll longTermRuns.
  longTermPreview: (data)       => api.post('/gtplSaathi/long-term/preview', data),
  longTermStart:   (runId)      => api.post(`/gtplSaathi/long-term/runs/${runId}/start`),
  longTermCancel:  (runId)      => api.post(`/gtplSaathi/long-term/runs/${runId}/cancel`),
  longTermResume:  (runId)      => api.post(`/gtplSaathi/long-term/runs/${runId}/resume`),
  longTermRuns:    (customerId) => api.get('/gtplSaathi/long-term/runs', { params: { customerId } }),
  // GTPL vs CRM — reconciles active CATV customers' CRM data against the live GTPL Saathi
  // record. getCrmCheckFields returns the catalog of comparable fields (the page builds both
  // its "what to compare" picker and its result columns from it); runCrmCheck starts a
  // background pass over the chosen fields (one real GTPL lookup per customer, can take a
  // while); poll getCrmCheckLatest for progress/counters.
  // Box Stock — spare (balance) boxes per entity from GTPL Saathi's entity-wise STB report.
  getBoxStock:          ()            => api.get('/gtplSaathi/box-stock'),
  getEntityCheck:       ()            => api.get('/gtplSaathi/entity-check'),
  refreshBoxStock:      ()            => api.post('/gtplSaathi/box-stock/refresh', {}, { timeout: 90000 }),
  getBoxSerials:        ()            => api.get('/gtplSaathi/box-stock/serials'),
  startBoxSerials:      ()            => api.post('/gtplSaathi/box-stock/serials', {}),
  // Expiry Check — CRM expiry date vs live GTPL end date, per customer type (regular/dtd/amp).
  getExpiryCheckCount:  (types)       => api.get('/gtplSaathi/expiry-check/count', { params: { types: (types || []).join(',') } }),
  runExpiryCheck:       (durationTypes) => api.post('/gtplSaathi/expiry-check/run', { durationTypes }),
  stopExpiryCheck:      ()            => api.post('/gtplSaathi/expiry-check/stop'),
  getExpiryCheckLatest: ()            => api.get('/gtplSaathi/expiry-check/latest'),
  getExpiryCheckRows:   (params)      => api.get('/gtplSaathi/expiry-check/rows', { params }),
  getCrmCheckFields:  ()            => api.get('/gtplSaathi/crm-check/fields'),
  runCrmCheck:        (fields)      => api.post('/gtplSaathi/crm-check/run', { fields }),
  // Stops the currently running check or bulk mobile fix — doesn't abort a GTPL request
  // already in flight, but nothing further starts once it's seen.
  stopCrmCheck:       ()            => api.post('/gtplSaathi/crm-check/stop'),
  getCrmCheckLatest:  ()            => api.get('/gtplSaathi/crm-check/latest'),
  getCrmCheckRows:    (params)      => api.get('/gtplSaathi/crm-check/rows', { params }),
  // Latest cached GTPL expiry date / box status for a set of customer ids, from the last GTPL
  // vs CRM check's saved rows — no live GTPL request. Used by the STB Inventory tab.
  getCrmCheckByCustomers: (ids)     => api.get('/gtplSaathi/crm-check/by-customers', { params: { ids: ids.join(',') } }),
  // Real write (raises a GTPL eSupport ticket) — same 60s timeout as modifyProfile above.
  fixCrmCheckMobile:  (rowId)       => api.post(`/gtplSaathi/crm-check/rows/${rowId}/fix-mobile`, {}, { timeout: 60000 }),
  fixAllCrmCheckMobiles: (runId)    => api.post('/gtplSaathi/crm-check/fix-all-mobiles', { runId }),
  // Reverse direction — entity/box model/box number, copied FROM GTPL INTO the CRM record.
  // Plain local DB write, no GTPL call, so no extended timeout needed.
  // Name/address go to GTPL like mobile, but straight through Modify.aspx with no eSupport
  // ticket — and both in one save when both disagree.
  fixCrmCheckProfile:    (rowId)    => api.post(`/gtplSaathi/crm-check/rows/${rowId}/fix-profile`, {}, { timeout: 60000 }),
  fixAllCrmCheckProfiles: (runId)   => api.post('/gtplSaathi/crm-check/fix-all-profiles', { runId }),
  fixCrmCheckInCrm:      (rowId)    => api.post(`/gtplSaathi/crm-check/rows/${rowId}/fix-in-crm`),
  fixAllCrmCheckInCrm:   (runId)    => api.post('/gtplSaathi/crm-check/fix-all-in-crm', { runId }),
  // GTPL Not In CRM — opposite direction from GTPL vs CRM above: scrapes GTPL Saathi's own
  // "Subscriber List with Active Package" report (no bulk API, so this is a real background
  // page-by-page scrape) and finds accounts active there with no matching Account No. in the
  // CRM at all. Same run/poll shape as the crm-check endpoints above.
  runOrphanCheck:      ()          => api.post('/gtplSaathi/orphan-check/run'),
  stopOrphanCheck:     ()          => api.post('/gtplSaathi/orphan-check/stop'),
  getOrphanCheckLatest: ()         => api.get('/gtplSaathi/orphan-check/latest'),
  getOrphanCheckRows:  (params)    => api.get('/gtplSaathi/orphan-check/rows', { params }),
  // GTPL 90 Days (Account) — every account on GTPL's "INACTIVE SUBSCRIBER LIST REPORT" with its
  // box + entity, and a staff-entered STB-issue pending/done flag (kept across refreshes).
  // Same run/poll shape as the orphan check above.
  runInactiveRefresh:      ()          => api.post('/gtplSaathi/inactive-accounts/run'),
  stopInactiveRefresh:     ()          => api.post('/gtplSaathi/inactive-accounts/stop'),
  getInactiveLatest:       ()          => api.get('/gtplSaathi/inactive-accounts/latest'),
  getInactiveOptions:      ()          => api.get('/gtplSaathi/inactive-accounts/options'),
  getInactiveRows:         (params)    => api.get('/gtplSaathi/inactive-accounts/rows', { params }),
  setInactiveStbIssue:     (id, status) => api.patch(`/gtplSaathi/inactive-accounts/rows/${id}/stb-issue`, { status }),
  // GTPL STB Mismatch — narrower cousin of GTPL vs CRM: for accounts that DO match a CRM
  // customer, flags a box model/serial mismatch, read off the same bulk scrape as the orphan
  // check above (no extra per-customer GTPL round-trip). Same run/poll shape.
  runStbCheck:         ()          => api.post('/gtplSaathi/stb-check/run'),
  stopStbCheck:        ()          => api.post('/gtplSaathi/stb-check/stop'),
  getStbCheckLatest:   ()          => api.get('/gtplSaathi/stb-check/latest'),
  getStbCheckRows:     (params)    => api.get('/gtplSaathi/stb-check/rows', { params }),
  fixStbCheckInCrm:    (rowId)     => api.post(`/gtplSaathi/stb-check/rows/${rowId}/fix-in-crm`),
  fixAllStbCheckInCrm: (runId)     => api.post('/gtplSaathi/stb-check/fix-all-in-crm', { runId }),
  // A la Carte Bulk — adds one channel (e.g. free AASTHA, 12 Months) to every active customer
  // in the background; preview first, then place (see gtplAlacarteService).
  getAlacarteSummary:  (channel, types) => api.get('/gtplSaathi/alacarte/summary', { params: { channel, types } }),
  getAlacarteCatalog:  (refresh)   => api.get('/gtplSaathi/alacarte/catalog', { params: { refresh: refresh || undefined } }),
  getAlacarteCustomers: (types)    => api.get('/gtplSaathi/alacarte/customers', { params: { types } }),
  restartAlacarteRound: (channelName, keepOld) => api.post('/gtplSaathi/alacarte/restart', { channelName, keepOld }),
  startAlacarteRun:    (body)      => api.post('/gtplSaathi/alacarte/run', body),
  stopAlacarteRun:     ()          => api.post('/gtplSaathi/alacarte/stop'),
  pauseAlacarteRun:    ()          => api.post('/gtplSaathi/alacarte/pause'),
  resumeAlacarteRun:   ()          => api.post('/gtplSaathi/alacarte/resume'),
  getAlacarteLatest:   ()          => api.get('/gtplSaathi/alacarte/latest'),
  getAlacarteRows:     (params)    => api.get('/gtplSaathi/alacarte/rows', { params }),
  getAlacarteScreenshot: (rowId)   => api.get(`/gtplSaathi/alacarte/rows/${rowId}/screenshot`, { responseType: 'blob' }),
  getChangeOrderSummary: ()        => api.get('/gtplSaathi/change-order/summary'),
  startChangeOrderRun:   (body)    => api.post('/gtplSaathi/change-order/run', body),
  pauseChangeOrderRun:   ()        => api.post('/gtplSaathi/change-order/pause'),
  resumeChangeOrderRun:  ()        => api.post('/gtplSaathi/change-order/resume'),
  stopChangeOrderRun:    ()        => api.post('/gtplSaathi/change-order/stop'),
  getChangeOrderLatest:  ()        => api.get('/gtplSaathi/change-order/latest'),
  getChangeOrderRows:    (params)  => api.get('/gtplSaathi/change-order/rows', { params }),
  getAutoAlign:          ()        => api.get('/gtplSaathi/change-order/auto-align'),
  setAutoAlign:          (enabled) => api.put('/gtplSaathi/change-order/auto-align', { enabled }),
};

export const playboxtvAPI = {
  getPacks:      ()               => api.get('/playboxtv/packs'),
  syncPacks:     ()               => api.post('/playboxtv/packs/sync'),
  updatePack:    (id, data)       => api.put(`/playboxtv/packs/${id}`, data),
  assign:        (customerId, packId, scheduledFor) => api.post(`/playboxtv/customers/${customerId}/assign`, { packId, scheduledFor }),
  getStatus:     (customerId)     => api.get(`/playboxtv/customers/${customerId}/status`),
  getHistory:    (customerId)     => api.get(`/playboxtv/customers/${customerId}/history`),
  cancel:        (customerId)     => api.delete(`/playboxtv/customers/${customerId}`),
  cancelScheduled: (customerId)   => api.delete(`/playboxtv/customers/${customerId}/scheduled`),
};

// ─── Import ────────────────────────────────────────────────────────────────────
export const importAPI = {
  getTemplateUrl:  () => `${API_URL}/import/template`,
  previewSheet:    (url) => api.post('/import/preview-sheet', { url }),
  validate:        (data) => api.post('/import/validate', data),
  run:             (data) => api.post('/import/run', data),
};

// ─── Sync Import (update existing customers' fields from a file, matched by ────
// PPPoE username or CATV account number) ───────────────────────────────────────
// service: 'broadband' (PPPoE-username match, expiry), 'catv' (account-number
// match, expiry) or 'catvHardware' (account-number match, STB serial/model/entity code)
const SYNC_PATHS = { catv: 'catv/', catvHardware: 'catv-hardware/' };
export const syncImportAPI = {
  getTemplateUrl: (service) => `${API_URL}/sync-import/${SYNC_PATHS[service] || ''}template`,
  validate:       (data, service) => api.post(`/sync-import/${SYNC_PATHS[service] || ''}validate`, data),
  run:            (data, service) => api.post(`/sync-import/${SYNC_PATHS[service] || ''}run`, data),
  exportCurrent:  (params, service) => api.get(`/sync-import/${SYNC_PATHS[service] || ''}export`, { params }),
};

// ─── Payment History Import (bulk historical payment ledger load) ────────────
// service: 'catv' (default, pre-existing routes) | 'broadband' (new, /broadband/ prefix) —
// mirrors syncImportAPI's URL-prefix convention, just the other way round since catv was
// the original/default service here.
const hiPrefix = (service) => (service === 'broadband' ? 'broadband/' : '');
export const historyImportAPI = {
  validate:       (data, service) => api.post(`/history-import/${hiPrefix(service)}validate`, data),
  run:            (data, service) => api.post(`/history-import/${hiPrefix(service)}run`, data),
  getPending:     (params, service) => api.get(`/history-import/${hiPrefix(service)}pending`, { params }),
  linkPending:    (id, customerId, service) => api.post(`/history-import/${hiPrefix(service)}pending/${id}/link`, { customerId }),
  discardPending: (id, service) => api.post(`/history-import/${hiPrefix(service)}pending/${id}/discard`),
  bulkLinkPerfect: (data, service) => api.post(`/history-import/${hiPrefix(service)}pending/bulk-link-perfect`, data),
  getLastImport:  (service) => api.get(`/history-import/${hiPrefix(service)}last-import`),
  importForCustomer: (customerId, records) => api.post(`/history-import/customer/${customerId}`, { records }),
  importBix42Sheet: (customerId, data) => api.post(`/history-import/customer/${customerId}/bix42-sheet`, data),
  saveUnknownLayoutSample: (data) => api.post('/history-import/unknown-layout-sample', data),
};

// ─── Balance Update (bulk export/edit/reimport of outstanding balances) ──────
export const balanceUpdateAPI = {
  export:   (params) => api.get('/balance-update/export', { params }),
  validate: (data) => api.post('/balance-update/validate', data),
  apply:    (data) => api.post('/balance-update/apply', data),
};

// ─── App Options (addable dropdowns) ──────────────────────────────────────────
export const optionsAPI = {
  get:    (key)        => api.get(`/options/${key}`),
  add:    (key, value) => api.post(`/options/${key}`, { value }),
  remove: (key, value) => api.delete(`/options/${key}/${encodeURIComponent(value)}`),
};

// ─── Fiber Mapping ─────────────────────────────────────────────────────────────
export const fiberAPI = {
  getTree:         ()         => api.get('/fiber/tree'),
  getPONPorts:     ()         => api.get('/fiber/pon-ports'),
  createPONPort:   (data)     => api.post('/fiber/pon-ports', data),
  updatePONPort:   (port, data) => api.put(`/fiber/pon-ports/${port}`, data),
  deletePONPort:   (port)     => api.delete(`/fiber/pon-ports/${port}`),
  deletePONPortTree: (port)   => api.delete(`/fiber/pon-ports/${port}/tree`),
  getOlt:          ()         => api.get('/fiber/olt'),
  updateOltCoordinates: (coordinates) => api.put('/fiber/olt/coordinates', { coordinates }),
  getSplitters:    ()         => api.get('/fiber/splitters'),
  createSplitter:  (data)     => api.post('/fiber/splitters', data),
  updateSplitter:  (id, data) => api.put(`/fiber/splitters/${id}`, data),
  updateSplitterCoordinates: (id, coordinates) => api.put(`/fiber/splitters/${id}/coordinates`, { coordinates }),
  deleteSplitter:  (id)       => api.delete(`/fiber/splitters/${id}`),
  getMappings:     ()         => api.get('/fiber/mappings'),
  getMapping:      (serial)   => api.get(`/fiber/mappings/${encodeURIComponent(serial)}`),
  autoLink:        ()         => api.post('/fiber/mappings/auto-link', {}, { timeout: 120000 }),
  syncOnus:        ()         => api.post('/fiber/mappings/sync-onus', {}, { timeout: 60000 }),
  saveMapping:     (serial, data) => api.put(`/fiber/mappings/${encodeURIComponent(serial)}`, data),
  deleteMapping:   (serial)   => api.delete(`/fiber/mappings/${encodeURIComponent(serial)}`),
  getRoutes:       ()         => api.get('/fiber/routes'),
  createRoute:     (data)     => api.post('/fiber/routes', data),
  updateRoute:     (id, data) => api.put(`/fiber/routes/${id}`, data),
  deleteRoute:     (id)       => api.delete(`/fiber/routes/${id}`),
};

// ─── Change Ledger (audit trail) ───────────────────────────────────────────────
export const logsAPI = {
  getAll:  (params) => api.get('/logs', { params }),
  restore: (id)     => api.post(`/logs/${id}/restore`),
};

// ─── Ads (customer app promo banner) ───────────────────────────────────────────
export const adsAPI = {
  getAll: () => api.get('/ads'),
  getActive: () => api.get('/ads/active'),
  getServerImages: () => api.get('/ads/server-images'),
  createFromServerImage: (data) => api.post('/ads', data),
  // Content-Type must be explicitly unset (not just omitted) — the shared `api`
  // instance defaults to 'application/json', which axios v1 treats as "serialize
  // this FormData as JSON" instead of sending it as multipart. Setting it to
  // undefined here overrides that default so the browser generates its own
  // multipart Content-Type with the required boundary.
  // Images can take longer than the default 15s timeout on a slow connection —
  // same reasoning as customerAPI.uploadDocuments.
  upload: (formData) => api.post('/ads', formData, { headers: { 'Content-Type': undefined }, timeout: 120000 }),
  update: (id, data) => api.put(`/ads/${id}`, data),
  delete: (id) => api.delete(`/ads/${id}`),
};

// ─── ACS (TR-069 ONT management) ────────────────────────────────────────────────
export const acsAPI = {
  getStatus: () => api.get('/acs/status'),
  getConnectionInfo: () => api.get('/acs/connection-info'),
  getDevices: (params) => api.get('/acs/devices', { params }),
  getDeviceByCustomer: (customerId) => api.get(`/acs/devices/by-customer/${customerId}`),
  getDevice: (serial, params) => api.get(`/acs/devices/${serial}`, { params }),
  wifi: (serial, data) => api.post(`/acs/devices/${serial}/wifi`, data, { timeout: 45000 }),
  wan: (serial, data) => api.post(`/acs/devices/${serial}/wan`, data),
  linkCustomer: (serial, customerId) => api.post(`/acs/devices/${serial}/link-customer`, { customerId }),
  autoLinkCustomer: (serial) => api.post(`/acs/devices/${serial}/auto-link`, {}),
  unlinkCustomer: (serial) => api.post(`/acs/devices/${serial}/unlink-customer`, {}),
  reboot: (serial) => api.post(`/acs/devices/${serial}/reboot`, {}),
  factoryReset: (serial) => api.post(`/acs/devices/${serial}/factory-reset`, {}),
  refresh: (serial, groups) => api.post(`/acs/devices/${serial}/refresh`, groups ? { groups } : {}),
  setParams: (serial, values) => api.post(`/acs/devices/${serial}/params`, { values }),
  getParams: (serial, names) => api.post(`/acs/devices/${serial}/get-params`, { names }),
  firmware: (serial, data) => api.post(`/acs/devices/${serial}/firmware`, data),
  ping: (serial, host) => api.post(`/acs/devices/${serial}/ping`, { host }),
  getPingResults: (serial) => api.get(`/acs/devices/${serial}/ping-results`),
  speedtestDownload: (serial, size) => api.post(`/acs/devices/${serial}/speedtest-download`, size ? { size } : {}, { timeout: 35000 }),
  speedtestUpload: (serial, size) => api.post(`/acs/devices/${serial}/speedtest-upload`, size ? { size } : {}, { timeout: 35000 }),
  getSpeedtestResults: (serial) => api.get(`/acs/devices/${serial}/speedtest-results`),
  getSpeedtestProgress: (serial) => api.get(`/acs/devices/${serial}/speedtest-progress`),
  deleteDevice: (serial) => api.delete(`/acs/devices/${serial}`),
  getPendingTasks: () => api.get('/acs/tasks/pending'),
  getMacAcl: (serial) => api.get(`/acs/devices/${serial}/mac-acl`),
  blockDevice: (serial, mac, wlanInstance) => api.post(`/acs/devices/${serial}/mac-block`, { mac, wlanInstance }, { timeout: 55000 }),
  unblockDevice: (serial, mac) => api.post(`/acs/devices/${serial}/mac-unblock`, { mac }, { timeout: 40000 }),
};

// ─── GST Billing (admin only, behind a second password) ─────────────────────
// The unlock token lives in sessionStorage (cleared when the tab closes), never localStorage.
let gstToken = '';
try { gstToken = sessionStorage.getItem('gstUnlock') || ''; } catch { /* storage blocked */ }
export const gstSetToken = (t) => {
  gstToken = t || '';
  try { if (t) sessionStorage.setItem('gstUnlock', t); else sessionStorage.removeItem('gstUnlock'); } catch { /* ignore */ }
};
const gh = () => (gstToken ? { headers: { 'x-gst-unlock': gstToken } } : {});
export const gstAPI = {
  status: () => api.get('/gst/status', gh()),
  setPassword: (password) => api.post('/gst/set-password', { password }),
  unlock: (password) => api.post('/gst/unlock', { password }),
  lock: () => api.post('/gst/lock', {}, gh()),
  resetPassword: (adminLoginPassword, newPassword) => api.post('/gst/reset-password', { adminLoginPassword, newPassword }),
  states: () => api.get('/gst/states', gh()),
  company: () => api.get('/gst/company', gh()),
  saveCompany: (b) => api.put('/gst/company', b, gh()),
  parties: (params) => api.get('/gst/parties', { params, ...gh() }),
  saveParty: (id, b) => (id ? api.put(`/gst/parties/${id}`, b, gh()) : api.post('/gst/parties', b, gh())),
  removeParty: (id) => api.delete(`/gst/parties/${id}`, gh()),
  items: (params) => api.get('/gst/items', { params, ...gh() }),
  saveItem: (id, b) => (id ? api.put(`/gst/items/${id}`, b, gh()) : api.post('/gst/items', b, gh())),
  removeItem: (id) => api.delete(`/gst/items/${id}`, gh()),
  invoices: (params) => api.get('/gst/invoices', { params, ...gh() }),
  createInvoice: (b) => api.post('/gst/invoices', b, gh()),
  updateInvoice: (id, b) => api.put(`/gst/invoices/${id}`, b, gh()),
  deleteInvoice: (id) => api.delete(`/gst/invoices/${id}`, gh()),
  cancelInvoice: (id, reason) => api.post(`/gst/invoices/${id}/cancel`, { reason }, gh()),
  notes: (params) => api.get('/gst/notes', { params, ...gh() }),
  createNote: (b) => api.post('/gst/notes', b, gh()),
  pdf: (kind, id) => api.get(`/gst/${kind === 'invoice' ? 'invoices' : 'notes'}/${id}/pdf`, { responseType: 'blob', timeout: 60000, ...gh() }),
  report: (month) => api.get('/gst/reports', { params: { month }, ...gh() }),
  exportReport: (params) => api.get('/gst/reports/export', { params, responseType: 'blob', ...gh() }),
  downloadCaExcel: (month) => api.get('/gst/reports/ca-excel', { params: { month }, responseType: 'blob', timeout: 60000, ...gh() }),
  setNextNumber: (nextNumber) => api.put('/gst/company/next-number', { nextNumber }, gh()),
  shareInvoice: (id, to) => api.post(`/gst/invoices/${id}/whatsapp`, { to }, { timeout: 90000, ...gh() }),
  shareNote: (id, to) => api.post(`/gst/notes/${id}/whatsapp`, { to }, { timeout: 90000, ...gh() }),
  delivery: () => api.get('/gst/delivery', { timeout: 60000, ...gh() }),
  saveDelivery: (b) => api.put('/gst/delivery/settings', b, gh()),
  deliverySend: (month) => api.post('/gst/delivery/send', { month }, { timeout: 120000, ...gh() }),
  deliveryReopen: (month) => api.post('/gst/delivery/reopen', { month }, gh()),
  deliveryTest: () => api.post('/gst/delivery/test', {}, { timeout: 120000, ...gh() }),
};

// ── AI assistant (floating button). Sends the GST unlock token so GST tools can run when the section is unlocked. ──
export const assistantAPI = {
  chat: (message, chatId) => api.post('/assistant/chat', { message, chatId }, { timeout: 100000, ...gh() }),
  confirm: (id) => api.post(`/assistant/actions/${id}/confirm`, {}, { timeout: 60000, ...gh() }),
  cancel: (id) => api.post(`/assistant/actions/${id}/cancel`, {}),
  chats: () => api.get('/assistant/chats'),
  chatById: (id) => api.get(`/assistant/chats/${id}`),
  audit: () => api.get('/assistant/audit'),
  tts: (text, lang) => api.post('/assistant/tts', { text, lang }, { responseType: 'blob', timeout: 20000 }),
  settings: () => api.get('/assistant/settings'),
  saveSettings: (body) => api.put('/assistant/settings', body),
};

// ── Claude Code chat (admin only) ──
export const claudeChatEventsUrl = (id) => `${API_URL}/claude-chat/sessions/${encodeURIComponent(id)}/events`;
export const claudeChatAPI = {
  status: () => api.get('/claude-chat/status'),
  setEnabled: (enabled) => api.put('/claude-chat/settings', { enabled }),
  commands: () => api.get('/claude-chat/commands'),
  sessions: () => api.get('/claude-chat/sessions'),
  createSession: (title) => api.post('/claude-chat/sessions', { title }),
  session: (id) => api.get(`/claude-chat/sessions/${id}`),
  deleteSessions: (ids) => api.post('/claude-chat/sessions/delete', { ids }),
  send: (id, text) => api.post(`/claude-chat/sessions/${id}/messages`, { text }),
  stop: (id) => api.post(`/claude-chat/sessions/${id}/stop`, {}),
  setAuto: (id, on) => api.put(`/claude-chat/sessions/${id}/auto`, { on }),
  decide: (approvalId, decision) => api.post(`/claude-chat/approvals/${approvalId}`, { decision }),
};

export default api;
